import * as Sentry from '@sentry/nextjs'
import { push } from '@socialgouv/matomo-next'
import Big from 'big.js'
import { fromUnit } from 'metronome-utils'
import useTranslation from 'next-translate/useTranslation'
import { useContext, useState } from 'react'

import TransactionContext from '../components/TransactionContext'
import getErrorKey, { isWalletTimeoutError } from '../utils/errorKeys'
import { txStatuses } from '../utils/transaction'

import { useIsMounted } from './useIsMounted'

const TRANSACTION_CONFIRMATION_TIMEOUT = 120000

class TransactionError extends Error {
  constructor(message) {
    super(message)
    this.name = 'TransactionError'
  }
}

const reportTransactionError = function (error, transactionOptions) {
  Sentry.setContext('Error object', error)
  Sentry.setContext('Transaction options', transactionOptions)
  Sentry.captureException(new TransactionError(error.message))
}

const useExecuteTransactions = function () {
  const { t } = useTranslation()
  const [isExecutingTransactions, setIsExecutingTransactions] = useState(false)
  const isMounted = useIsMounted()
  const { addTransactionStatus } = useContext(TransactionContext)

  const executeTransactions = function (emitter, options = {}) {
    setIsExecutingTransactions(true)

    const internalTransactionId = Date.now()
    const {
      onSuccess,
      onError,
      transactionOptions = {},
      analytics,
      events = {}
    } = options
    const { extendedStatus = {}, onResult } = events
    let pendingConfirmationTimeout

    if (transactionOptions.useEip1559 && analytics?.useEip1559) {
      window.gtag?.('event', analytics.useEip1559)
      push(['trackEvent', 'transaction', analytics.useEip1559])
    }

    emitter
      .on('transactions', function (transactions) {
        if (analytics?.events?.transactions) {
          window.gtag?.('event', analytics.events.transactions)
          push(['trackEvent', 'transaction', analytics.events.transactions])
        }

        if (isMounted) {
          addTransactionStatus({
            expectedFee: Big(fromUnit(transactions.expectedFee)).toFixed(4),
            internalTransactionId,
            message: t(
              `transaction-waiting-confirmation-${
                transactions.suffixes.includes('approve')
                  ? 'approve'
                  : transactions.suffixes[0]
              }`
            ),
            suffixes: transactions.suffixes,
            transactionStatus: txStatuses.Created,
            ...(extendedStatus ?? {})
          })
        }

        pendingConfirmationTimeout = setTimeout(function () {
          addTransactionStatus({
            internalTransactionId,
            message: t('transaction-pending-confirmation'),
            transactionStatus: txStatuses.PendingConfirmation
          })
        }, TRANSACTION_CONFIRMATION_TIMEOUT)

        setIsExecutingTransactions(false)

        return transactions.suffixes.forEach(function (suffix, idx) {
          emitter
            .on(`transactionHash-${suffix}`, function (transactionHash) {
              clearTimeout(pendingConfirmationTimeout)

              return addTransactionStatus({
                internalTransactionId,
                message: t(
                  `transaction-submitting-${
                    transactions.suffixes.includes('approve')
                      ? transactions.suffixes[1]
                      : transactions.suffixes[0]
                  }`
                ),
                transactionStatus: txStatuses.InProgress,
                [`transactionStatus-${idx}`]: 'waiting-to-be-mined',
                [`transactionHash-${idx}`]: transactionHash
              })
            })
            .on(`receipt-${suffix}`, ({ receipt }) =>
              addTransactionStatus({
                currentTransaction: idx + 1,
                internalTransactionId,
                [`transactionStatus-${idx}`]: receipt.status
                  ? txStatuses.Confirmed
                  : txStatuses.Canceled,
                [`transactionHash-${idx}`]: receipt.transactionHash
              })
            )
        })
      })
      .on('result', function (result) {
        clearTimeout(pendingConfirmationTimeout)

        const { fees, status } = result
        const { analyticsAction, ...extendedStatusResult } =
          onResult?.(result) ?? {}

        if (analytics?.events?.result) {
          window.gtag?.('event', analytics.events.result)
          push(['trackEvent', 'transaction', analytics.events.result])
        }

        addTransactionStatus({
          fee: Big(fromUnit(fees)).toFixed(4),
          internalTransactionId,
          message: t(`transaction-success-${extendedStatus.operation}`),
          transactionStatus: status
            ? txStatuses.Confirmed
            : txStatuses.Canceled,
          ...extendedStatusResult
        })

        onSuccess?.()
      })
      .once('error', function (error) {
        console.error(error) // Added temporarely to debug cross-chain ops
        clearTimeout(pendingConfirmationTimeout)

        setIsExecutingTransactions(false)

        if (isWalletTimeoutError(error)) {
          if (analytics?.events?.['error-timeout']) {
            window.gtag?.('event', analytics.events['error-timeout'])
            push([
              'trackEvent',
              'transaction',
              analytics.events['error-timeout']
            ])
          }

          addTransactionStatus({
            internalTransactionId,
            message: t('transaction-timeout-in-wallet'),
            transactionStatus: txStatuses.WalletTimeout
          })
          return
        }

        if (analytics?.events?.error) {
          window.gtag?.('event', analytics.events.error)
          push([
            'trackEvent',
            'transaction',
            `${analytics.events.error}: ${error?.message || 'unknown error'}`
          ])
        }

        const errorKey = getErrorKey(error)

        setTimeout(function () {
          addTransactionStatus({
            internalTransactionId,
            message: t(errorKey ? errorKey : 'error-unknown'),
            transactionStatus: txStatuses.Error
          })
        }, 1000)

        if (!errorKey) {
          reportTransactionError(error, transactionOptions)
        }

        onError?.()
      })
  }

  return {
    executeTransactions,
    isExecutingTransactions
  }
}

export { useExecuteTransactions }
